import { Box, Button, makeStyles } from '@material-ui/core';
import { Link, useParams } from '@reach/router';
import React, { useCallback, useState } from 'react';
import { useAsync } from 'react-async';
import ReactGA from 'react-ga';
import { useTranslation } from 'react-i18next';
import { generatePath } from 'react-router-dom';

import ConfirmationDialog from '../../../../../../components/ConfirmationDialog';
import LoadingContainer from '../../../../../../components/LoadingContainer';
import { api } from '../../../../../../core/api';
import { CONSTANTS } from '../../../../../../core/config/constants';
import { FontSizes } from '../../../../../../core/config/mainTheme';
import { border, toCssValue } from '../../../../../../core/utils/theme.utils';
import { getActiveDataset, getActiveDbVersion } from '../../../../../../core/utils/utils';
import { useAppGoogleReCaptcha } from '../../../../../../hooks/useAppGoogleReCaptcha';
import { ROUTES } from '../../../../../../routes';
import { useBenchmarkState } from '../../../../../Benchmark/Context/BenchmarkContext';
import { useCompaniesState } from '../../../../shared/CompanyContext/CompanyContext';
import { useErrorsDispatch } from '../../../../shared/ErrorContext/ErrorContext';
import { ErrorContextActionTypes } from '../../../../shared/ErrorContext/ErrorContext.types';
import ThemeBreadcrumbs from '../../../BreadCrumbs';
import { BreadCrumbLink } from '../../../BreadCrumbs/BreadCrumbs';
import ConnectionsChart from '../ConnectionsChart/ConnectionsChart';
import { useConnectionsState } from '../ConnectionsContext/ConnectionsContext';
import ConnectionDisplayCount from './ConnectionDisplayCount';
import ConnectionDisplayNavigation from './ConnectionDisplayNavigation';
import FormActionBox from './FormActionBox/FormActionBox';

interface Props {
  companyDetailsPath: string;
}

const ConnectionsBox: React.FC<Props> = ({ companyDetailsPath }) => {
  const { companyUid, dataset: datasetParam, dbVersion: dbVersionParam } = useParams<{
    companyUid?: string;
    dataset?: string;
    dbVersion?: string;
  }>();
  const [openConfDialog, setConfDialog] = useState(false);
  const { t } = useTranslation();
  const { path } = useConnectionsState();
  const classes = useStyles();
  const { executeRecaptcha } = useAppGoogleReCaptcha();
  const dispatchErrors = useErrorsDispatch();

  const { dataset, dbVersion } = useCompaniesState();
  const { activeBenchmark } = useBenchmarkState();
  const datasetSource = datasetParam ?? dataset;
  const dbVersionSource = dbVersionParam ?? dbVersion;
  const activeDataset = getActiveDataset(datasetSource, activeBenchmark);
  const activeDbVersion = getActiveDbVersion(dbVersionSource, activeBenchmark);

  const promiseFn = useCallback(
    () =>
      Promise.all([
        executeRecaptcha?.('search')!.then((token) =>
          api.company.getGeneralData(companyUid!, token, activeDataset, activeDbVersion),
        ),
      ]),
    [companyUid, executeRecaptcha, activeDataset, activeDbVersion],
  );
  const { data, isLoading } = useAsync({
    promiseFn,
    onReject: (error) => {
      dispatchErrors({
        type: ErrorContextActionTypes.SetError,
        payload: { error },
      });
      return Promise.reject(error);
    },
  });

  if (isLoading) {
    return <LoadingContainer />;
  }
  const historicalCompanyName = data?.[0]?.namesHistory?.length
    ? data[0].namesHistory[0].name + ` (${t('app:historical_name')})`
    : '';
  const companyName = data?.[0]?.generalDetails?.currentName ?? '';
  const displayName = companyName
    ? companyName
    : historicalCompanyName
    ? historicalCompanyName
    : CONSTANTS.UNKNOWN_NAME;

  const leafNodeId = path.length > 0 ? path[path.length - 1].id : companyUid;
  const linkDataset = datasetParam ?? dataset;
  const linkDbVersion = dbVersionParam ?? dbVersion;
  const detailsUrl =
    linkDataset && linkDbVersion
      ? generatePath(ROUTES.financialData.company, {
          dataset: linkDataset,
          dbVersion: linkDbVersion,
          companyUid: leafNodeId,
        })
      : `/database/financial-data/${leafNodeId}`;

  const breadCrumbs: BreadCrumbLink[] = [
    {
      text: t('connections:form:companies'),
    },
    {
      path: companyDetailsPath,
      text: t('app:connectionsDetails') + displayName,
    },
    {
      text: t('app:connections'),
    },
  ];

  return (
    <>
      <Box className={classes.toolbar}>
        <ThemeBreadcrumbs items={breadCrumbs} />
        <Box className={classes.toolbarRightSide}>
          <Box className={classes.itemsContainer}>
            <ConnectionDisplayCount />
          </Box>
          <FormActionBox buttonTitle={t('connections:actionBoxTitle')} />
        </Box>
      </Box>
      <Box className={classes.connectionsBox}>
        <Box className={classes.connectionsHeaderBox}>
          <Box className={classes.boxItems}>
            {leafNodeId !== companyUid && (
              <Button onClick={() => setConfDialog(true)} className={classes.backlink}>
                {t('companies:viewCompanyDetails')}
              </Button>
            )}
            {leafNodeId === companyUid && (
              <Link to={companyDetailsPath} className={classes.backlink}>
                {t('companies:backToCompanyDetails')}
              </Link>
            )}
            <ConfirmationDialog
              open={openConfDialog}
              handleClose={() => {
                setConfDialog(false);
              }}
              handleConfirmOk={async () => {
                setConfDialog(false);
                ReactGA.event({ category: 'CONNECTIONS_CHART', action: 'COMPANY_DETAILS_CLICK' });
                window.open(detailsUrl, '_blank', 'noopener')?.focus();
              }}
              text="app:confirmationText:companies:connectionChartNewTab"
            />
          </Box>
          <Box className={classes.boxItems}>
            <ConnectionDisplayNavigation />
          </Box>
        </Box>
        <ConnectionsChart />
      </Box>
    </>
  );
};

const useStyles = makeStyles((theme) => ({
  connectionsBox: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    flexGrow: 1,
    padding: toCssValue([theme.spacing(0), theme.spacing(4), theme.spacing(5)]),
  },
  connectionsHeaderBox: {
    display: 'flex',
    flexDirection: 'row',
    width: '100%',
    borderLeft: border(theme.palette.divider),
    borderRight: border(theme.palette.divider),
  },
  itemsContainer: {
    display: 'flex',
    marginRight: '10px',
    alignItems: 'center',
  },
  backlink: {
    display: 'flex',
    flexWrap: 'nowrap',
    alignItems: 'center',
    textDecoration: 'none',
    textTransform: 'uppercase',
    color: theme.palette.primary.contrastText,
    backgroundColor: theme.palette.primary.main,
    fontSize: FontSizes.size.smallest,
    marginRight: theme.spacing(6),
    marginLeft: theme.spacing(4),
    padding: '6px 8px',
    borderRadius: '4px',
    lineHeight: '1.75',
    cursor: 'pointer',
    '& .MuiSvgIcon-root': {
      marginRight: theme.spacing(2),
      width: theme.spacing(8),
    },
    '&.bold': {
      fontWeight: 'bold',
    },
    '&:hover': {
      backgroundColor: theme.palette.primary.main,
    },
  },
  boxItems: {
    display: 'flex',
    flexWrap: 'nowrap',
    alignItems: 'center',
    height: '50px',
  },
  companyName: {
    color: theme.palette.secondary.dark,
    fontSize: FontSizes.size.smallest,
    padding: toCssValue([theme.spacing(0), theme.spacing(5)]),
    textTransform: 'uppercase',
  },
  toolbar: {
    display: 'flex',
    alignItems: 'stretch',
    justifyContent: 'space-between',
    height: CONSTANTS.TOOLBAR_HEIGHT,
    backgroundColor: theme.palette.primary.dark,
    color: theme.palette.background.paper,
    padding: '0 18px 0 0',
  },
  toolbarRightSide: {
    display: 'flex',
  },
}));

export default ConnectionsBox;
